import mergeLocalizedContent from './mergeLocalizedContent.js';

export const BLOG_TRANSLATION_LANGUAGES = Object.freeze([
  'tr',
  'es',
  'fr',
  'de',
  'it',
  'id',
  'th',
  'bn',
  'hi',
  'pt'
]);

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const mergeArrayByIndex = (baseArray = [], overrideArray = []) => {
  const maxLength = Math.max(baseArray.length, overrideArray.length);
  const output = [];

  for (let index = 0; index < maxLength; index += 1) {
    const baseItem = baseArray[index];
    const overrideItem = overrideArray[index];

    if (overrideItem == null) {
      if (baseItem != null) output.push(baseItem);
      continue;
    }

    if (baseItem == null) {
      output.push(overrideItem);
      continue;
    }

    if (isPlainObject(baseItem) && isPlainObject(overrideItem)) {
      output.push(mergeLocalizedContent(baseItem, overrideItem));
      continue;
    }

    output.push(overrideItem);
  }

  return output;
};

export const mergeBlogPostContent = (base, override) => {
  if (!override) return base;
  if (!isPlainObject(base)) return override;
  if (!isPlainObject(override)) return mergeLocalizedContent(base, override);

  const {
    sections: overrideSections,
    howTo: overrideHowTo,
    faqs: overrideFaqs,
    ...restOverride
  } = override;

  const output = mergeLocalizedContent(base, restOverride);

  if (Array.isArray(overrideSections)) {
    output.sections = mergeArrayByIndex(
      Array.isArray(base.sections) ? base.sections : [],
      overrideSections
    );
  }

  if (isPlainObject(overrideHowTo)) {
    const {
      steps: overrideHowToSteps,
      ...restHowToOverride
    } = overrideHowTo;
    const baseHowTo = isPlainObject(base.howTo) ? base.howTo : {};
    output.howTo = mergeLocalizedContent(baseHowTo, restHowToOverride);
    if (Array.isArray(overrideHowToSteps)) {
      output.howTo.steps = mergeArrayByIndex(
        Array.isArray(baseHowTo.steps) ? baseHowTo.steps : [],
        overrideHowToSteps
      );
    }
  }

  if (Array.isArray(overrideFaqs)) {
    output.faqs = mergeArrayByIndex(
      Array.isArray(base.faqs) ? base.faqs : [],
      overrideFaqs
    );
  }

  return output;
};

export const getPostLanguageTranslation = (post, language = 'en') => {
  if (language === 'en') return null;
  if (!post || typeof post !== 'object') return null;
  const translations = isPlainObject(post.translations) ? post.translations : {};
  const override = translations[language];
  return isPlainObject(override) ? override : null;
};

export const buildDynamicBlogTranslationMap = (posts = [], language = 'en') => {
  if (language === 'en') return {};
  return (Array.isArray(posts) ? posts : []).reduce((acc, post) => {
    if (!post?.slug) return acc;
    const override = getPostLanguageTranslation(post, language);
    if (override) acc[post.slug] = override;
    return acc;
  }, {});
};

export const mergeBlogTranslationMaps = (...maps) => (
  maps.reduce((acc, map) => {
    if (!map || typeof map !== 'object') return acc;
    return { ...acc, ...map };
  }, {})
);

export const getBlogAvailableLanguagesForPost = (post, staticTranslationMaps = {}) => {
  const languages = ['en'];
  if (!post?.slug) return languages;

  BLOG_TRANSLATION_LANGUAGES.forEach((language) => {
    const hasStaticTranslation = Boolean(staticTranslationMaps?.[language]?.[post.slug]);
    const hasDynamicTranslation = Boolean(getPostLanguageTranslation(post, language));
    if (hasStaticTranslation || hasDynamicTranslation) {
      languages.push(language);
    }
  });

  return languages;
};
